import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const styles = {
  dropzone: (file, dragOver) => ({
    border: '1.5px dashed',
    borderColor: file ? 'success.main' : dragOver ? 'primary.main' : 'divider',
    borderRadius: 2.5,
    bgcolor: file ? 'rgba(70,191,131,0.12)' : dragOver ? 'action.hover' : 'transparent',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
    py: 2,
    px: 2,
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': { borderColor: file ? 'success.main' : 'text.disabled' },
    mb: 1.5,
    textAlign: 'center',
  }),
  fileInfoContainer: { display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', px: 0.5 },
  fileIcon: { fontSize: 26, color: 'success.main', flexShrink: 0 },
  fileDetails: { flexGrow: 1, minWidth: 0 },
  fileName: { color: 'success.main' },
  fileMetadata: { display: 'flex', alignItems: 'center', gap: 0.5 },
  fileDeleteButton: { color: 'text.disabled', flexShrink: 0, '&:hover': { color: 'text.secondary', bgcolor: 'transparent' } },
  progressBar: { mt: 0.75, borderRadius: 1, height: 3 },
  emptyStateContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 },
  uploadIconWrapper: {
    bgcolor: theme => theme.bruno.colors.inactive,
    p: 1.5,
    borderRadius: '0.75rem',
    mb: 0.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadIcon: { fontSize: 32, color: 'text.disabled' },
  emptyStateTitle: { color: 'text.primary', fontWeight: 500, fontSize: '0.875rem' },
  emptyStateBrowse: {
    color: 'text.secondary',
    fontSize: '0.75rem',
    cursor: 'pointer',
    '&:hover': { color: 'text.primary' },
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    mb: 2,
    px: 1.5,
    py: 1.25,
    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(245,158,11,0.12)' : '#fffbeb',
    border: '1px solid',
    borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(245,158,11,0.3)' : '#fde68a',
    borderRadius: 1.5,
  },
  warningIcon: { fontSize: 16, color: '#d97706', mt: '2px', flexShrink: 0 },
  warningText: { color: 'warning.main', lineHeight: 1.5, fontSize: '0.8rem' },
  button: {
    borderRadius: 1,
    textTransform: 'none',
    fontWeight: 500,
    py: 1,
    boxShadow: 'none',
    '&:hover': { boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
  },
  buttonIcon: { fontSize: 16 },
};

// `warning` is the expected file format, which differs per flow (bare product
// keys vs. the filled-in task download) — so the caller owns that copy. Omit it
// to render no banner; this component knows nothing about file formats.
export default function ExcelDropzone({ uploading = false, disabled = false, onUpload, actionLabel, warning }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const handleDeleteFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!file) return;
    await onUpload(file);
  };

  return (
    <>
      {/* ── Warning banner ── */}
      {warning && (
        <Box sx={styles.warningBanner}>
          <WarningAmberIcon sx={styles.warningIcon} />
          <Typography variant="body2" sx={styles.warningText}>
            {warning}
          </Typography>
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* ── Dropzone ── */}
      <Box
        onClick={() => !uploading && !disabled && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!uploading && !disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { if (!uploading && !disabled) handleDrop(e); }}
        sx={styles.dropzone(file, dragOver)}
      >
        {file ? (
          <Box sx={styles.fileInfoContainer}>
            <InsertDriveFileOutlinedIcon sx={styles.fileIcon} />
            <Box sx={styles.fileDetails}>
              <Typography variant="body2" fontWeight={500} noWrap sx={styles.fileName}>
                {file.name}
              </Typography>
              <Box sx={styles.fileMetadata}>
                <Typography variant="caption" color="text.disabled">
                  {(file.size / 1024).toFixed(0)} KB
                </Typography>
                {uploading && (
                  <>
                    <Typography variant="caption" color="text.disabled">·</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('bruno.excel.uploading', 'Uploading...')}
                    </Typography>
                  </>
                )}
              </Box>
              {uploading && <LinearProgress color="success" sx={styles.progressBar} />}
            </Box>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); if (!uploading) handleDeleteFile(); }}
              sx={styles.fileDeleteButton}
              disabled={uploading}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ) : (
          <Box sx={styles.emptyStateContainer}>
            <Box sx={styles.uploadIconWrapper}>
              <CloudUploadIcon sx={styles.uploadIcon} />
            </Box>
            <Typography sx={styles.emptyStateTitle}>
              {t('bruno.excel.dropzone_title', 'Drag & drop to upload')}
            </Typography>
            <Typography
              sx={styles.emptyStateBrowse}
              onClick={(e) => { e.stopPropagation(); if (!disabled && !uploading) fileInputRef.current?.click(); }}
            >
              {t('bruno.excel.browse', 'or browse')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Upload button ── */}
      <Button
        type="button"
        variant="contained"
        size="small"
        startIcon={<CloudUploadIcon sx={styles.buttonIcon} />}
        onClick={handleUpload}
        disabled={uploading || disabled || !file}
        fullWidth
        sx={styles.button}
      >
        {actionLabel ?? t('bruno.excel.upload', 'Upload')}
      </Button>
    </>
  );
}
